<template>
    <div class='p-4'>
        <div class='mt-2 mb-2'>
            <p class="ds-section-title-medium">System or machine challenge</p>
            <el-input
                v-model="inputChallenge"
                @keyup="readChallenge"
                size="large"
                :class="{ warning: challenge.length > 0 && !isValid}" />
            </div>
        <span v-if="challenge.length > 0 && !isValid">
            <p class="text-small text-warning">The challenge is not valid.</p>
        </span>
        <div v-else class="pt-2">
            <p class="text-small" v-if="fields.length > 0">Challenge metadata</p>
            <table class="staticTable text-small mt-2 mb-2">
                <tr v-for="field in fields" :key="field.name">
                    <td><strong>{{ field.name }}</strong></td>
                    <td>{{ field.value }}</td>
                </tr>
            </table>
            <div v-if="isValid" >
                <el-button class="mt-2 mb-2" type="primary" @click="createLicense">GENERATE LICENSE KEY
                    <el-icon :size="15" class="ml-1">
                        <Right />
                    </el-icon>
                </el-button>
                <div v-if="generatedLicenseKey !== ''">
                    <p class="text-small">Generated license key</p>
                    <div
                        class="d-flex flex-row mt-2 mb-2">
                        <el-input
                            id="licenseKeyField"
                            type="textarea"
                            :rows="1"
                            v-model="generatedLicenseKey"
                            :readonly="true"
                            :clearable="false">
                        </el-input>
                        <el-button class="ml-2 mr-2" style="height: fit-content" type="primary" circle @click="copyLicenseToClipBoard">
                            <el-icon :size="15">
                                <DocumentCopy />
                            </el-icon>
                        </el-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
:deep() {
    .warning {
        .el-input__wrapper {
                box-shadow: 0 0 0 1px $--warning-color !important;
            }
    }
}

</style>

<script lang='ts'>
import { mapState } from 'vuex';
import { CreateLicense, ReadChallenge, ResetChallenge, ResetLicense } from '@/store';
import { ElMessage } from 'element-plus';
import { DocumentCopy } from '@element-plus/icons-vue';
import { Right } from '@element-plus/icons-vue';
import { trackError } from '@/util/telemetry';

import { defineComponent } from 'vue';

export default defineComponent({
    name: 'challenges',
    data() {
        return {
            inputChallenge: '',
            generatedLicense: '',
            lastmessageTimeStamp: Math.floor(Date.now() / 1000),
        };
    },
    created(): any {
      this.inputChallenge = this.$store.state.challenge.challenge;
      this.generatedLicense = this.$store.state.challenge.generatedLicenseKey;
    },
    methods: {
        async readChallenge() {
            try {
                if (this.inputChallenge.length > 0) {
                    await this.$store.dispatch(new ReadChallenge(this.inputChallenge));
                } else {
                    this.$store.dispatch(new ResetChallenge());
                }
            } catch (error) {
                const newTimeStamp = Math.floor(Date.now() / 1000);
                if ( newTimeStamp - this.lastmessageTimeStamp  > 2) {
                    this.lastmessageTimeStamp = newTimeStamp;
                    ElMessage({
                        message: 'Cannot read challenge: ' + error,
                        type: 'error',
                        showClose: true,
                        duration: 7000});
                    trackError('Cannot read challenge: ' + String(error), 'LP-ERR-FE-003', 'readChallenge');
                }
            }
        },
        async createLicense() {
            try {
                this.$store.dispatch(new ResetLicense());
                if (this.$store.state.challenge.isValid) {
                    await this.$store.dispatch(new CreateLicense(this.inputChallenge));
                    this.generatedLicense = this.$store.state.challenge.generatedLicenseKey;
                }
            } catch (error) {
                const newTimeStamp = Math.floor(Date.now() / 1000);
                if ( newTimeStamp - this.lastmessageTimeStamp  > 2) {
                    this.lastmessageTimeStamp = newTimeStamp;
                    ElMessage({
                        message: 'Cannot create license: ' + error,
                        type: 'error',
                        showClose: true,
                        duration: 7000});
                    trackError('Cannot create license: ' + String(error), 'LP-ERR-FE-004', 'createLicense');
                }
            }
        },
        async copyLicenseToClipBoard() {
            try {
                const copyText = document.getElementById('licenseKeyField') as HTMLInputElement;
                if ( copyText !== null) {
                    copyText.select();
                    copyText.setSelectionRange(0, 99999);
                    document.execCommand('copy');
                }
            } catch (error) {
                const newTimeStamp = Math.floor(Date.now() / 1000);
                if ( newTimeStamp - this.lastmessageTimeStamp  > 2) {
                    this.lastmessageTimeStamp = newTimeStamp;
                    ElMessage({
                        message: 'Cannot copy license to clip board: ' + error,
                        type: 'error',
                        showClose: true,
                        duration: 7000});
                    trackError('Cannot copy license to clip board: ' + String(error), 'LP-ERR-FE-005', 'copyLicenseToClipBoard');
                }
            }
        },
    },
    computed: {
        ...mapState('challenge', ['challenge', 'isValid', 'fields', 'generatedLicenseKey']),
    },
    components: {
        DocumentCopy,
        Right,
    },
});
</script>
